import { FC } from "react";
import { Box, Drawer, IconButton, Stack, Typography, Chip, Tooltip } from "@mui/material";
import { Close, OpenInNew } from "@mui/icons-material";
import { tokens } from "@/tokens";

export type AiEnrichmentCitation = {
  sourceType: string;
  identifier: string;
  url: string;
  excerpt?: string;
  relevance?: string;
  verified: boolean;
};

export type AiEnrichment = {
  bucketKey: string;
  patternId: string;
  direction: 'refund' | 'compliance';
  status: 'queued' | 'running' | 'done' | 'partial' | 'failed';
  modelVersion?: string;
  iterations?: number;
  lastEnrichedAt?: string;
  computedAt?: string;
  startedAt?: string;
  lastHeartbeatAt?: string;
  currentStage?: 'sampling' | 'researching' | 'synthesizing' | 'verifying';
  currentTool?: string;
  title?: string;
  summary?: string;
  recommendedAction?: string;
  confidence?: 'high' | 'medium' | 'low';
  verdict?: 'confirmed' | 'systemError' | 'ambiguous';
  verdictReason?: string;
  citations?: AiEnrichmentCitation[];
  sampledFindingIds?: string[];
  error?: string;
};

const splitChecklist = (text: string): string[] =>
  text
    .split(/\n+|[•·]\s+|^\s*\d+\.\s+/m)
    .map((s) => s.trim())
    .filter(Boolean);

const AiAnalysisDrawer: FC<{
  enrichment: AiEnrichment | null;
  bucketName: string;
  bucketAbsDollars: number;
  onClose: () => void;
}> = ({ enrichment, bucketName, bucketAbsDollars, onClose }) => {
  const open = !!enrichment;
  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: { xs: "100vw", md: "min(900px, 90vw)" }, height: "100%", display: "flex", flexDirection: "column", bgcolor: tokens.bg.surface }}>
        <Box sx={{ px: 3, py: 1.5, display: "flex", alignItems: "center", justifyContent: "space-between", bgcolor: tokens.bg.hover, borderBottom: `1px solid ${tokens.border.default}` }}>
          <Typography sx={{ fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.bold }}>
            AI Analysis
          </Typography>
          <IconButton onClick={onClose} size="small">
            <Close sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
        {enrichment && (
          <Box sx={{ flex: 1, overflow: "auto", p: 3 }}>
            <Typography sx={{ fontSize: tokens.fontSize.lg, fontWeight: tokens.fontWeight.heavy, mb: 0.5 }}>
              {enrichment.title || bucketName}
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
              {(() => {
                const v = enrichment.verdict;
                const cfg = v === 'systemError'
                  ? { label: 'Likely false positive', bg: `${tokens.status.danger}15`, color: tokens.status.danger }
                  : v === 'ambiguous'
                    ? { label: 'Ambiguous — needs docs', bg: `${tokens.status.warning}15`, color: tokens.status.warning }
                    : v === 'confirmed'
                      ? { label: 'Confirmed discrepancy', bg: `${tokens.status.success}15`, color: tokens.status.success }
                      : null;
                return cfg ? (
                  <Chip label={cfg.label} size="small" sx={{ fontWeight: tokens.fontWeight.bold, bgcolor: cfg.bg, color: cfg.color, border: `1px solid ${cfg.color}40` }} />
                ) : null;
              })()}
              <Chip label={enrichment.direction === 'refund' ? 'Refund opportunity' : 'Compliance owe'} size="small" sx={{ fontWeight: tokens.fontWeight.bold, bgcolor: enrichment.direction === 'refund' ? `${tokens.status.success}15` : `${tokens.status.danger}15`, color: enrichment.direction === 'refund' ? tokens.status.success : tokens.status.danger }} />
              {enrichment.confidence && (
                <Chip label={`Confidence: ${enrichment.confidence}`} size="small" sx={{ fontWeight: tokens.fontWeight.semibold }} />
              )}
              <Typography sx={{ fontWeight: tokens.fontWeight.semibold, fontSize: tokens.fontSize.sm }}>
                ${bucketAbsDollars.toLocaleString()}
              </Typography>
            </Stack>

            {enrichment.verdict === 'systemError' && (
              <Box sx={{ p: 1.75, mb: 2, bgcolor: `${tokens.status.danger}10`, border: `1px solid ${tokens.status.danger}40`, borderRadius: tokens.radius.md }}>
                <Typography sx={{ fontSize: tokens.fontSize["2xs"], color: tokens.status.danger, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: tokens.fontWeight.bold, mb: 0.5 }}>
                  Calc-engine review recommended
                </Typography>
                <Typography sx={{ fontSize: tokens.fontSize.sm, color: tokens.text.body }}>
                  {enrichment.verdictReason || 'The deterministic engine flagged this incorrectly.'}
                </Typography>
              </Box>
            )}
            {enrichment.verdict === 'ambiguous' && (
              <Box sx={{ p: 1.75, mb: 2, bgcolor: `${tokens.status.warning}10`, border: `1px solid ${tokens.status.warning}40`, borderRadius: tokens.radius.md }}>
                <Typography sx={{ fontSize: tokens.fontSize["2xs"], color: tokens.status.warning, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: tokens.fontWeight.bold, mb: 0.5 }}>
                  Need additional documentation
                </Typography>
                <Typography sx={{ fontSize: tokens.fontSize.sm, color: tokens.text.body }}>
                  {enrichment.verdictReason || 'Cannot determine without additional docs.'}
                </Typography>
              </Box>
            )}
            {enrichment.verdict === 'confirmed' && enrichment.verdictReason && (
              <Box sx={{ p: 1.5, mb: 2, bgcolor: `${tokens.status.success}08`, border: `1px solid ${tokens.status.success}30`, borderRadius: tokens.radius.md, fontSize: tokens.fontSize.sm, color: tokens.text.body }}>
                {enrichment.verdictReason}
              </Box>
            )}

            {enrichment.status === 'partial' && (
              <Box sx={{ p: 1.5, mb: 2, bgcolor: `${tokens.status.warning}15`, border: `1px solid ${tokens.status.warning}40`, borderRadius: tokens.radius.md, fontSize: tokens.fontSize.sm, color: tokens.status.warning }}>
                External research unavailable — partial result.
              </Box>
            )}

            {enrichment.summary && (
              <Box sx={{ mb: 3 }}>
                <Typography sx={{ fontSize: tokens.fontSize["2xs"], color: tokens.text.muted, textTransform: "uppercase", letterSpacing: 0.4, mb: 1 }}>Summary</Typography>
                {enrichment.summary.split(/\n\n+/).map((p, i) => (
                  <Typography key={i} sx={{ fontSize: tokens.fontSize.sm, mb: 1.5, lineHeight: 1.6 }}>{p}</Typography>
                ))}
              </Box>
            )}

            {enrichment.recommendedAction && (
              <Box sx={{ mb: 3 }}>
                <Typography sx={{ fontSize: tokens.fontSize["2xs"], color: tokens.text.muted, textTransform: "uppercase", letterSpacing: 0.4, mb: 1 }}>Recommended action</Typography>
                <Stack spacing={1}>
                  {splitChecklist(enrichment.recommendedAction).map((step, i) => (
                    <Stack key={i} direction="row" spacing={1} alignItems="flex-start">
                      <Typography sx={{ fontSize: tokens.fontSize.sm, color: tokens.brand.primary, fontWeight: tokens.fontWeight.bold, lineHeight: 1.6 }}>{i + 1}.</Typography>
                      <Typography sx={{ fontSize: tokens.fontSize.sm, lineHeight: 1.6 }}>{step}</Typography>
                    </Stack>
                  ))}
                </Stack>
              </Box>
            )}

            {(enrichment.citations || []).length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography sx={{ fontSize: tokens.fontSize["2xs"], color: tokens.text.muted, textTransform: "uppercase", letterSpacing: 0.4, mb: 1 }}>Citations</Typography>
                <Stack spacing={1}>
                  {(enrichment.citations || []).map((c, i) => (
                    <Box key={i} sx={{ p: 1.5, border: `1px solid ${tokens.border.default}`, borderRadius: tokens.radius.md }}>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                        <Chip label={c.sourceType} size="small" sx={{ height: 18, fontSize: tokens.fontSize["2xs"] }} />
                        <Typography sx={{ fontSize: tokens.fontSize.xs, fontWeight: tokens.fontWeight.semibold, fontFamily: "monospace" }}>{c.identifier}</Typography>
                        <Box sx={{ flex: 1 }} />
                        <Tooltip title="Open source">
                          <IconButton size="small" component="a" href={c.url} target="_blank" rel="noopener noreferrer">
                            <OpenInNew sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                      {c.excerpt && (
                        <Typography sx={{ fontSize: tokens.fontSize.xs, color: tokens.text.secondary, fontStyle: "italic", mb: 0.5 }}>
                          "{c.excerpt}"
                        </Typography>
                      )}
                      {c.relevance && (
                        <Typography sx={{ fontSize: tokens.fontSize.xs, color: tokens.text.muted }}>
                          {c.relevance}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}

            <Box sx={{ borderTop: `1px solid ${tokens.border.default}`, pt: 2, mt: 2 }}>
              <Typography sx={{ fontSize: tokens.fontSize["2xs"], color: tokens.text.muted, textTransform: "uppercase", letterSpacing: 0.4, mb: 1 }}>Audit trail</Typography>
              <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ rowGap: 0.5, fontSize: tokens.fontSize.xs, color: tokens.text.muted }}>
                <span>Model: <code>{enrichment.modelVersion}</code></span>
                <span>Iterations: {enrichment.iterations ?? 0}</span>
                <span>Sampled findings: {(enrichment.sampledFindingIds || []).length}</span>
                {enrichment.computedAt && <span>Computed: {new Date(enrichment.computedAt).toLocaleString()}</span>}
              </Stack>
            </Box>
          </Box>
        )}
      </Box>
    </Drawer>
  );
};

export default AiAnalysisDrawer;
